import React, { useState } from 'react';
import { snippets, searchSnippets, getAllSnippets } from '../utils/codeSnippets';

const SnippetBrowser = ({ isOpen, onClose, onInsertSnippet }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSnippet, setSelectedSnippet] = useState(null);

  const categories = [
    { id: 'all', name: 'All Snippets', icon: '📦' },
    { id: 'react', name: 'React', icon: '⚛️' },
    { id: 'api', name: 'API Calls', icon: '🌐' },
    { id: 'express', name: 'Express', icon: '🚂' },
    { id: 'fastapi', name: 'FastAPI', icon: '🐍' },
    { id: 'mongodb', name: 'MongoDB', icon: '🍃' },
    { id: 'tailwind', name: 'Tailwind CSS', icon: '🎨' },
    { id: 'testing', name: 'Testing', icon: '🧪' },
    { id: 'async', name: 'Async/Await', icon: '⏳' }
  ];

  const getFilteredSnippets = () => {
    let snippetsToShow;

    if (searchQuery) {
      snippetsToShow = searchSnippets(searchQuery);
    } else if (selectedCategory === 'all') {
      snippetsToShow = getAllSnippets();
    } else {
      snippetsToShow = (snippets[selectedCategory] || []).map(s => ({
        ...s,
        category: selectedCategory
      }));
    }

    return snippetsToShow;
  };

  const filteredSnippets = getFilteredSnippets();

  const handleInsert = (snippet) => {
    onInsertSnippet(snippet.code);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-6xl h-5/6 flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Code Snippets</h2>
            <p className="text-sm text-gray-400 mt-1">Quick insert common code patterns</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 hover:bg-gray-800 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-700">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search snippets..."
            className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Categories */}
          <div className="w-64 border-r border-gray-700 overflow-y-auto">
            {categories.map(category => {
              const count = selectedCategory === category.id
                ? filteredSnippets.length
                : category.id === 'all'
                ? getAllSnippets().length
                : (snippets[category.id] || []).length;

              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSearchQuery('');
                    setSelectedSnippet(null);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{category.icon}</span>
                    <span className="font-medium">{category.name}</span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    selectedCategory === category.id
                      ? 'bg-blue-700'
                      : 'bg-gray-700'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Snippet List */}
          <div className="flex-1 flex overflow-hidden">
            <div className="w-80 border-r border-gray-700 overflow-y-auto">
              {filteredSnippets.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p className="text-lg mb-2">No snippets found</p>
                  <p className="text-sm">Try a different search or category</p>
                </div>
              ) : (
                filteredSnippets.map((snippet, index) => (
                  <div
                    key={`${snippet.category}-${snippet.id}`}
                    onClick={() => setSelectedSnippet(snippet)}
                    className={`p-4 border-b border-gray-800 cursor-pointer transition-colors ${
                      selectedSnippet?.id === snippet.id
                        ? 'bg-gray-800 border-l-4 border-l-blue-500'
                        : 'hover:bg-gray-850'
                    }`}
                  >
                    <div className="font-medium text-white mb-1">{snippet.name}</div>
                    <div className="text-sm text-gray-400">{snippet.description}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-1 bg-gray-700 rounded text-gray-300">
                        {snippet.category}
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-700 rounded text-gray-300">
                        {snippet.language}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Preview */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {selectedSnippet ? (
                <>
                  <div className="p-4 border-b border-gray-700 bg-gray-950">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {selectedSnippet.name}
                    </h3>
                    <p className="text-sm text-gray-400">{selectedSnippet.description}</p>
                  </div>
                  <div className="flex-1 overflow-auto p-4 bg-gray-950">
                    <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap">
                      {selectedSnippet.code}
                    </pre>
                  </div>
                  <div className="p-4 border-t border-gray-700 bg-gray-900">
                    <button
                      onClick={() => handleInsert(selectedSnippet)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
                    >
                      Insert Snippet
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    <p className="text-lg">Select a snippet to preview</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-700 bg-gray-950">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{filteredSnippets.length} snippets available</span>
            <span>💡 Tip: Use command palette (Ctrl+P) for quick access</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SnippetBrowser;
